import { h } from "preact";
import type { JSX } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { DATA_EVENTS, INTERNAL_EVENTS, SDK_EVENTS } from "../../../constants/events.cnst";
import { ClassMapConstanst, IdMapConstant, POLARITY_VS_STYLES, SENTI_POLARITY_MAP } from "../../../constants/projConsts";
import "./sentimentScore.scss";
import * as echarts from 'echarts/core';
import type { EChartsType } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { GridComponent, VisualMapComponent, MarkPointComponent } from 'echarts/components';

// Initialize echarts
echarts.use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  VisualMapComponent,
  MarkPointComponent
]);

export function sentimentScore(props: any): JSX.Element {

    const rootServiceInstance = props.msgData?.hostInstance.rootService;
    const hostInstance = props.msgData?.hostInstance;
    const connectionDetails = rootServiceInstance.connectionDetails;
    const languageData = hostInstance?.languagedata
    // const canvas: any = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<EChartsType | null>(null);
    let currentPolarity = '';
    let lineChartData: any = [];
    const [mergeSentiOptions, setMergeSentiOptions] = useState<any>(null);

    useEffect(() => {

        initChart();

        hostInstance.on(DATA_EVENTS.REALTIME_SENTIMENT_RESPONSE, handleRealtimeSentiResponse);
        return () => {
            hostInstance.off(DATA_EVENTS.REALTIME_SENTIMENT_RESPONSE, handleRealtimeSentiResponse);
        }

    }, []);

    const initChart = useCallback(() => {
        if (chartRef.current && !chartInstanceRef.current) {
            chartInstanceRef.current = echarts.init(chartRef.current);
            
            // Set initial base options
            const baseOptions = getSentiAnalysisChartOptions();
            chartInstanceRef.current.setOption(baseOptions);
    
            // Handle window resize
            const handleResize = () => {
                chartInstanceRef.current?.resize();
            };
            window.addEventListener('resize', handleResize);
    
            return () => {
                window.removeEventListener('resize', handleResize);
                if (chartInstanceRef.current) {
                    chartInstanceRef.current.dispose();
                    chartInstanceRef.current = null;
                }
            };
        }
    }, []);

    // Handle updates
    useEffect(() => {
        if (chartInstanceRef.current && mergeSentiOptions) {
            const updatedOptions = { ...getSentiAnalysisChartOptions(), ...mergeSentiOptions };
            chartInstanceRef.current.setOption(updatedOptions, {
                notMerge: false,
                lazyUpdate: true
            });
        }
    }, [mergeSentiOptions]);

    const getExistingChartData = () => {
        if (chartInstanceRef.current) {
            const currentOption : any = chartInstanceRef.current.getOption();
            const existingData = currentOption?.series?.[0]?.data || [];           
            return existingData;
        }
        return [];
    };


    const handleRealtimeSentiResponse = (realtimeSentiData: any) => {
        if(realtimeSentiData && realtimeSentiData.sentiment && rootServiceInstance.prefsAccessObj?.prefs?.act?.some((item: any) => item?.smt?.isE === true)){
            let data = realtimeSentiData.sentiment;
            if (!rootServiceInstance.realtimeSentiData[connectionDetails.conversationId]) {
                rootServiceInstance.realtimeSentiData[connectionDetails.conversationId] = [];
            }
    
            let polarity = SENTI_POLARITY_MAP[data?.polarity];
            const POLARITY_RANGE = { min: 0, max: 10 };
            if (data?.polarity > POLARITY_RANGE.min && data?.polarity <= POLARITY_RANGE.max) {
                rootServiceInstance.realtimeSentiData[connectionDetails.conversationId].push(polarity);
                lineChartData = getExistingChartData();
                formatRealtimeSentiChartData(lineChartData);
            }
            if (data?.coarse) {
                currentPolarity = data.coarse;
            }

            let sentimentBtn = hostInstance?.chatEle?.querySelector(`#${IdMapConstant.SENTIMENT_BTN}`);
            hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, sentimentBtn, { event: SDK_EVENTS.SENTIMENT_EVENT, data: realtimeSentiData });
            if(sentimentBtn){
                sentimentBtn.classList.remove(ClassMapConstanst.HIDE_ELEMENT);
                sentimentBtn.innerHTML = rootServiceInstance.sanitizeHTML(currentPolarity + ' ' + POLARITY_VS_STYLES[currentPolarity]?.emoji);
                let classNameList = ['positive-title', 'negative-title', 'neutral-title'];
                let existingClass = sentimentBtn.classList;
                classNameList.forEach((className) => {
                    if(existingClass.contains(className)){
                        existingClass.remove(className);
                    }
                });
                sentimentBtn.classList.add(POLARITY_VS_STYLES[currentPolarity]?.className);
            }
        }

    }

    const formatRealtimeSentiChartData = (chartData: any) => {
        setSentimentAnalysisOption(chartData);
    }

    const getSentiAnalysisChartOptions = () => {
        return {
            xAxis: {
              splitLine: { show: false },
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: { show: false },
              boundaryGap: ['5%', '5%']
            },
            yAxis: {
              type: 'value',
              data: [-2, 0, 2],
              nameLocation: 'middle',
              axisLabel: {
                // formatter: val => this.showExtrems(val),
                show: false
              },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLine: { show: false },
              min: -2,
              boundaryGap: ['5%', '5%']
            },
            grid : {
              left: '3%',
              bottom: '3%',
              right: '3%',
              top: '7%',
              containLabel : true
            },
            visualMap: {
              show: false,
              dimension: 1,
              pieces: [
                {
                  lt: -0.25,
                  color: 'red',
                  label: '< (-0.25 Neg)'
                },
                {
                  gte: -0.25,
                  lte: 0,
                  color: 'grey',
                  label: '0 - (-0.25 Neu)'
                },
                {
                  gt: 0,
                  color: 'green',
                  label: '> 0 (Pos)'
                }
              ]
            },
      
            series: [
              {
                data: [[0,0]],
                type: 'line',
                smooth: true,
                showSymbol: false,
                lineStyle: {
                  width : 2
                },
                clip: false
              }
            ],
          };
    }
    

    const setSentimentAnalysisOption = (chartData: any) => {
        if (rootServiceInstance.realtimeSentiData[connectionDetails.conversationId]?.length > 0) {
            let polaritySum = 0;
            polaritySum = rootServiceInstance.realtimeSentiData[connectionDetails.conversationId].reduce((sum : number, ele : number) => sum + ele, 0);
            let average = polaritySum !== 0 ? (polaritySum / rootServiceInstance.realtimeSentiData[connectionDetails.conversationId]?.length) : 0;
            chartData.push([chartData.length, average]);
        }

        let mergeSentiOptions : any = {
            series: [
                {
                    data: chartData,
                    type: 'line',
                    smooth: true,
                    showSymbol: false,
                    lineStyle: {
                        width: 2
                    },
                    markPoint: {
                        animation: false,
                        symbol: 'circle',
                        data: [
                            {
                                xAxis: chartData[chartData.length - 1][0],
                                yAxis: chartData[chartData.length - 1][1],
                                symbolSize: 10
                            }
                        ]
                    }
                }
            ]
        }

        setMergeSentiOptions((prev : any) => ({...prev, ...mergeSentiOptions}));
       
    }

    // Function to update the chart with new data
    function updateChart(newData : any) {
        let chartData = newData; // Update chart data
        drawLineChart(chartData); // Redraw the chart with new data
    }

    // Function to clear the canvas
    function clearCanvas(ctx : any, canvas : any) {
        ctx.clearRect(0, 0, canvas?.current?.width, canvas?.current?.height);
    }

    function drawLineChart(chartData : any) {

      
    }
    return (
        <div className="aa-sentiment-popup">
            <div className="header-sentiment-chart a-position-relative a-flex a-align-items-center">
                <div className="a-flex a-align-items-center a-gap-4 a-width-100" title="Sentiment">
                    <p>{languageData?.SENTI_ANALY}</p>
                    {/* <div className="a-position-relative" ref={divRef}> */}
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" title="Sentiment">
                            <g clip-path="url(#clip0_169_554)">
                                <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#475467" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_169_554">
                                    <rect width="12" height="12" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                </div>
                <button className="close-sentiment-chart a-flex-shrink-0 a-flex a-align-items-center a-justify-content-center" id={IdMapConstant.SENTIMENT_POPUP_CLOSE_BTN}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M9 3L3 9M3 3L9 9" stroke="#475467" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>
            <div ref={chartRef} style={{ width: "100%", height: "200px", minWidth: "200px" }}></div>
        </div>
    );
}

export default sentimentScore; 
